import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export interface SeoData {
  // BASE
  title?: string;
  description?: string;
  keywords?: string;
  robots?: string;

  // URL / LINGUA
  canonicalUrl?: string; // può essere assoluto o relativo (/uffici)
  lang?: string;
  alternateLangs?: { lang: string; url: string }[];

  // OPEN GRAPH
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  ogSiteName?: string;
  ogLocale?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;

  // TWITTER
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterSite?: string;
  twitterCreator?: string;
  twitterImageAlt?: string;

  // EXTRA
  themeColor?: string;

  // JSON-LD
  jsonLd?: any | any[];
}

@Injectable({ providedIn: 'root' })
export class SeoService {

  // ✅ metti qui il dominio ufficiale
  private readonly SITE_BASE_URL = 'https://samipulizie.it';

  // ✅ defaults brand
  private readonly DEFAULTS = {
    lang: 'it',
    ogLocale: 'it_IT',
    ogType: 'website',
    ogSiteName: 'SAMI Pulizie',
    twitterCard: 'summary_large_image',
    themeColor: '#0f2960',
    robots: 'index,follow'
  };

  // lista tag che vogliamo “controllare” e pulire tra una pagina e l’altra
  private readonly MANAGED_META_NAMES = [
    'description',
    'keywords',
    'robots',
    'twitter:card',
    'twitter:title',
    'twitter:description',
    'twitter:image',
    'twitter:site',
    'twitter:creator',
    'twitter:image:alt',
    'theme-color'
  ];

  private readonly MANAGED_META_PROPERTIES = [
    'og:title',
    'og:description',
    'og:image',
    'og:url',
    'og:type',
    'og:site_name',
    'og:locale',
    'og:image:width',
    'og:image:height'
  ];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Aggiorna TITLE + META + OG + TWITTER + CANONICAL + JSON-LD
   */
  updateTags(data: SeoData): void {
    const isBrowser = isPlatformBrowser(this.platformId);

    // ✅ prima puliamo i tag “gestiti” se non vengono passati (evita residui tra route)
    this.cleanupManagedTags(data);

    // -------------------------------
    // TITLE
    // -------------------------------
    if (data.title) this.titleService.setTitle(data.title);

    // -------------------------------
    // META BASE
    // -------------------------------
    this.setMetaName('description', data.description);
    this.setMetaName('keywords', data.keywords);
    this.setMetaName('robots', data.robots || this.DEFAULTS.robots);

    // theme-color (utile su mobile)
    this.setMetaName('theme-color', data.themeColor || this.DEFAULTS.themeColor);

    // -------------------------------
    // CANONICAL (auto)
    // -------------------------------
    const canonicalAbsolute = this.toAbsoluteUrl(
      data.canonicalUrl || (isBrowser ? window.location.pathname : '/')
    );

    this.setCanonical(canonicalAbsolute);

    // -------------------------------
    // OPEN GRAPH
    // -------------------------------
    const ogTitle = data.ogTitle || data.title;
    const ogDescription = data.ogDescription || data.description;

    // og:url: usa ogUrl > canonicalUrl > calcolato
    const ogUrl = this.toAbsoluteUrl(data.ogUrl || data.canonicalUrl || (isBrowser ? window.location.pathname : '/'));

    this.setMetaProperty('og:title', ogTitle);
    this.setMetaProperty('og:description', ogDescription);
    this.setMetaProperty('og:url', ogUrl);

    this.setMetaProperty('og:type', data.ogType || this.DEFAULTS.ogType);
    this.setMetaProperty('og:site_name', data.ogSiteName || this.DEFAULTS.ogSiteName);
    this.setMetaProperty('og:locale', data.ogLocale || this.DEFAULTS.ogLocale);

    if (data.ogImage) {
      this.setMetaProperty('og:image', this.toAbsoluteUrl(data.ogImage));
      if (data.ogImageWidth) this.setMetaProperty('og:image:width', data.ogImageWidth);
      if (data.ogImageHeight) this.setMetaProperty('og:image:height', data.ogImageHeight);
    }

    // -------------------------------
    // TWITTER
    // -------------------------------
    const twitterTitle = data.twitterTitle || data.title;
    const twitterDescription = data.twitterDescription || data.description;
    const twitterImage = data.twitterImage || data.ogImage;

    this.setMetaName('twitter:card', data.twitterCard || this.DEFAULTS.twitterCard);
    if (twitterTitle) this.setMetaName('twitter:title', twitterTitle);
    if (twitterDescription) this.setMetaName('twitter:description', twitterDescription);
    if (twitterImage) this.setMetaName('twitter:image', this.toAbsoluteUrl(twitterImage));
    if (data.twitterSite) this.setMetaName('twitter:site', data.twitterSite);
    if (data.twitterCreator) this.setMetaName('twitter:creator', data.twitterCreator);
    if (data.twitterImageAlt) this.setMetaName('twitter:image:alt', data.twitterImageAlt);

    // -------------------------------
    // LINGUA HTML
    // -------------------------------
    if (isBrowser) {
      this.document.documentElement.setAttribute('lang', data.lang || this.DEFAULTS.lang);
    }

    // -------------------------------
    // HREFLANG (se li usi)
    // -------------------------------
    if (data.alternateLangs) {
      this.setHrefLangs(data.alternateLangs.map(x => ({
        lang: x.lang,
        url: this.toAbsoluteUrl(x.url)
      })));
    }

    // -------------------------------
    // JSON-LD
    // -------------------------------
    this.setJsonLd(data.jsonLd);
  }

  // =====================================================
  // PRIVATE UTILITIES
  // =====================================================

  private setMetaName(name: string, content?: string): void {
    if (!content) return;
    this.metaService.updateTag({ name, content });
  }

  private setMetaProperty(property: string, content?: string): void {
    if (!content) return;
    this.metaService.updateTag({ property, content });
  }

  private setCanonical(url: string): void {
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private setHrefLangs(entries: { lang: string; url: string }[]): void {
    this.document
      .querySelectorAll("link[rel='alternate'][data-seo-hreflang]")
      .forEach(el => el.remove());

    entries.forEach(entry => {
      const link = this.document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = entry.lang;
      link.href = entry.url;
      link.setAttribute('data-seo-hreflang', 'true');
      this.document.head.appendChild(link);
    });
  }

  private setJsonLd(jsonLd: any | any[] | undefined): void {
    this.document
      .querySelectorAll("script[type='application/ld+json'][data-seo-jsonld]")
      .forEach(el => el.remove());

    if (!jsonLd) return;

    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

    items.forEach(item => {
      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.text = JSON.stringify(item);
      this.document.head.appendChild(script);
    });
  }

  /**
   * Converte URL relative in assolute.
   * - "/uffici" -> "https://samipulizie.it/uffici"
   * - "assets/og.jpg" -> "https://samipulizie.it/assets/og.jpg"
   * - "https://..." resta uguale
   */
  private toAbsoluteUrl(url?: string): string {
    if (!url) return this.SITE_BASE_URL + '/';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;

    // assicura leading slash
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return `${this.SITE_BASE_URL}${normalized}`;
  }

  /**
   * Rimuove meta “gestiti” che non vengono impostati nella chiamata corrente.
   * Serve a non lasciare residui tra una route e l'altra.
   */
  private cleanupManagedTags(data: SeoData): void {
    const isBrowser = isPlatformBrowser(this.platformId);
    // per Meta service non serve browser, ma per sicurezza non tocchiamo document qui
    // (la rimozione via Meta non richiede document direttamente)

    // names
    const providedNames = new Set<string>();
    if (data.description) providedNames.add('description');
    if (data.keywords) providedNames.add('keywords');
    if (data.robots) providedNames.add('robots');
    if (data.twitterCard) providedNames.add('twitter:card');
    if (data.twitterTitle || data.title) providedNames.add('twitter:title');
    if (data.twitterDescription || data.description) providedNames.add('twitter:description');
    if (data.twitterImage || data.ogImage) providedNames.add('twitter:image');
    if (data.twitterSite) providedNames.add('twitter:site');
    if (data.twitterCreator) providedNames.add('twitter:creator');
    if (data.twitterImageAlt) providedNames.add('twitter:image:alt');
    providedNames.add('theme-color'); // sempre default

    this.MANAGED_META_NAMES.forEach(name => {
      if (!providedNames.has(name)) {
        this.metaService.removeTag(`name='${name}'`);
      }
    });

    // properties
    const providedProps = new Set<string>();
    if (data.ogTitle || data.title) providedProps.add('og:title');
    if (data.ogDescription || data.description) providedProps.add('og:description');
    providedProps.add('og:url'); // sempre calcolato
    providedProps.add('og:type'); // default
    providedProps.add('og:site_name'); // default
    providedProps.add('og:locale'); // default

    if (data.ogImage) {
      providedProps.add('og:image');
      if (data.ogImageWidth) providedProps.add('og:image:width');
      if (data.ogImageHeight) providedProps.add('og:image:height');
    }

    this.MANAGED_META_PROPERTIES.forEach(prop => {
      if (!providedProps.has(prop)) {
        this.metaService.removeTag(`property='${prop}'`);
      }
    });
  }
}
